import React, { FC, ReactNode, useState } from "react";
import { DeleteAction } from "../../../../features/deleteAction";
import { Message } from "../message/Message";
import classes from './header.module.scss'
import { useNavigate } from "react-router-dom";
import { STUDENT_UPDATE_ROUTE, STUDENTS_ROUTE } from "../../../../app/router/routes";
import { IStudentChange, IStudentData, studentService, useStudentActions } from "../../../../entities/student";
import editImg from '../../../../shared/lib/assets/edit.png'
import { useAppSelector } from "../../../../app/store/store";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { useGlobalLoadingActions } from "../../../../entities/globalLoading";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { FEATURES, hasFeature, hasFeatureGrant, useMyActions } from "../../../../entities/my";
import { Modal } from "../../../../shared/ui/modal";
import { ConfirmationAction } from "../../../../shared/ui/confirmationAction";
import { Dropdown } from "../../../../shared/ui/dropdown";
import { MyTextarea } from "../../../../shared/ui/myTextarea";
import { MyButton, MyButtonSecondary } from "../../../../shared/ui/button";
import { LabelBadges } from '../../../../shared/ui/labels'
import { SanctionSequenceResetAction } from "../../../../features/sanctionSequenceReset";

interface IProps {
    student: IStudentData;
    setStudent: React.Dispatch<React.SetStateAction<IStudentData | undefined>>;
    extraActions?: ReactNode;
}

export const Header: FC<IProps> = ({student, setStudent, extraActions}) => {

    const router = useNavigate()
    const {setIsLoading} = useGlobalLoadingActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const {my} = useAppSelector(s => s.myReducer)
    const [open, setOpen] = useState<boolean>(false)
    const [archiveComment, setArchiveComment] = useState<string>('')
    const [archiveCommentError, setArchiveCommentError] = useState<string>('')
    const [archiveConfirmOpen, setArchiveConfirmOpen] = useState<boolean>(false)
    const targetUserId = Number(student.user_id ?? 0)
    const canArchiveStudent = hasFeature(my, FEATURES.STUDENTS.CARD_ARCHIVE, targetUserId)
    const canUnarchiveStudent = hasFeature(my, FEATURES.STUDENTS.CARD_UNARCHIVE, targetUserId)
    const canArchiveAction = student.is_archive ? canUnarchiveStudent : canArchiveStudent
    const canEditStudent = hasFeature(my, FEATURES.STUDENTS.CARD_UPDATE, targetUserId)
        && hasFeatureGrant(my, FEATURES.IDM.STAFF_LIST)
        && hasFeature(my, FEATURES.WORK_TGS.LIST)
    const canDeleteStudent = hasFeature(my, FEATURES.STUDENTS.CARD_DELETE, targetUserId)
    const canResetSanctionSequence = hasFeature(my, FEATURES.STUDENTS.SANCTION_SEQUENCE_RESET, targetUserId)
    const canEditSanctionSequenceAction = canResetSanctionSequence
    const hasDropdownActions = Boolean(extraActions) || canEditSanctionSequenceAction
    
    const {setStudent: setStudentRedux} = useStudentActions()

    const onDelete = async () => {
        if(student.id){
            await studentService.delete(student.id)
            router(STUDENTS_ROUTE.path)
        }
    }

    const onEdit = () => {
        const studentEdit: IStudentChange = {
            id: student.id,
            first_name: student.first_name,
            last_name: student.last_name,
            middle_name: student.middle_name,
            phone: student.phone,
            tg: student.tg,
            cost_per_hour: student.cost_per_hour,
            user_id: student.user_id,
            tutor_name: student.tutor_name,
            parent_full_name: student.parent_full_name,
            parent_phone: student.parent_phone,
            parent_tg: student.parent_tg,
            work_tg_name: student.work_tg_name || '',
            work_tg_ids: student.work_tg_ids || [],
        }
        setStudentRedux(studentEdit)
        router(STUDENT_UPDATE_ROUTE.path)
    }

    const openArchiveModal = () => {
        setArchiveComment('')
        setArchiveCommentError('')
        setArchiveConfirmOpen(false)
        setOpen(true)
    }

    const onArchivePrepare = () => {
        if(!archiveComment.trim()){
            setArchiveCommentError('Укажите причину архивации')
            return
        }

        setArchiveConfirmOpen(true)
    }

    const onArchive = async () => {
        try{
            setIsLoading(true)
            if(student.is_archive){
                await studentService.unarchive(student.id)
                setStudent(student => ({
                    ...student,
                    is_archive: false,
                    archive_comment: '',
                }) as IStudentData)
                setGlobalMessage({message: 'Студент разархивирован', type: 'ok'})
            }
            else{
                await studentService.archive(student.id, archiveComment.trim())
                setStudent(student => ({
                    ...student,
                    is_archive: true,
                    archive_comment: archiveComment.trim(),
                }) as IStudentData)
                setGlobalMessage({message: 'Студент архивирован', type: 'ok'})
            }
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при архивировании или разархивировании студента', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
            setOpen(false)
        }
    }

    return (
        <section className={classes.header} data-dropdown-boundary>
            <section className={classes.heading}>
                <section className={classes.title}>
                    <span>{student.id}. {student.last_name} {student.first_name} {student.middle_name}</span>
                    <span
                        className={student.is_archive ? classes.archiveStatus : classes.activeStatus}
                        title={student.is_archive ? 'Ученик в архиве' : 'Ученик активен'}
                        aria-label={student.is_archive ? 'Ученик в архиве' : 'Ученик активен'}
                        data-status-tooltip={student.is_archive ? 'Ученик в архиве' : 'Ученик активен'}
                    >
                        {student.is_archive
                            ? <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6L18 18M18 6L6 18" /></svg>
                            : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5L9.5 17L19 7" /></svg>}
                    </span>
                </section>
                <section className={classes.warnings}>
                    {
                        student.is_balance_negative
                            &&
                        <Message 
                            type="balanceNegative"
                            sign="У родителя есть задолженность"
                        />
                    }
                    {
                        student.is_only_trial_finished
                            &&
                        <Message 
                            type="onlyTrial"
                            sign="Проведено только пробное занятие"
                        />
                    }
                    {
                        student.is_newbie
                            &&
                        <Message
                            type="newbie"
                            sign="Новичок"
                        />
                    }
                    <LabelBadges labels={student.labels} />
                </section>
            </section>
            <section className={classes.features}>
                {
                    hasDropdownActions &&
                    <section className={classes.actions}>
                        <Dropdown title={<span className={classes.actionSign}>Действия</span>}>
                            <section className={classes.dropdownActions}>
                                {
                                    extraActions &&
                                    <section className={classes.dropdownActionItem}>
                                        {extraActions}
                                    </section>
                                }
                                {canEditSanctionSequenceAction && (
                                    <section className={classes.dropdownActionItem}>
                                        <SanctionSequenceResetAction
                                            value={student.sanction_sequence_number}
                                            canEdit={canEditSanctionSequenceAction}
                                            onUpdate={(value) => studentService.updateSanctionSequence(student.id, value)}
                                            onSuccess={(value) => setStudent(current => current ? {
                                                ...current,
                                                sanction_sequence_number: value,
                                            } : current)}
                                        />
                                    </section>
                                )}
                            </section>
                        </Dropdown>
                    </section>
                }
                {
                    canEditStudent &&
                    <button
                        type="button"
                        onClick={onEdit}
                        className={classes.edit}
                        title="Редактировать ученика"
                        aria-label="Редактировать ученика"
                    >
                        <img src={editImg} alt="" />
                    </button>
                }
                {
                    canArchiveAction &&
                    <button
                        type="button"
                        className={`${classes.archiveToggle} ${student.is_archive ? classes.unarchive : ''}`}
                        onClick={openArchiveModal}
                        title={student.is_archive ? 'Разархивировать ученика' : 'Архивировать ученика'}
                        aria-label={student.is_archive ? 'Разархивировать ученика' : 'Архивировать ученика'}
                    >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 3V12" />
                            <path d="M7.05 5.05A8 8 0 1 0 16.95 5.05" />
                        </svg>
                    </button>
                }
                {
                    canDeleteStudent &&
                    <DeleteAction
                        successText="Студент удален"        
                        errorText="Ошибка при удалении студента"
                        onDelete={onDelete}
                        questionText="Точно хотите удалить Студента ?"
                    >
                        <button type="button" className={classes.deleteToggle} title="Удалить ученика" aria-label="Удалить ученика">
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M4 7H20" />
                                <path d="M10 11V17M14 11V17" />
                                <path d="M6 7L7 20H17L18 7" />
                                <path d="M9 7V4H15V7" />
                            </svg>
                        </button>
                    </DeleteAction>
                }
                <Modal setOpen={setOpen} open={open}>
                    {
                        student.is_archive || archiveConfirmOpen
                            ?
                        <ConfirmationAction
                            onClick={onArchive}
                            setOpen={setOpen}
                            title={`Вы точно хотите ${student.is_archive ? 'разархивировать' : 'архивировать'} студента?`}
                            type='send'
                        />
                            :
                        <section className={classes.archiveCommentModal}>
                            <h3>Причина архивации</h3>
                            <p>Укажите причину архивации. Она сохранится в карточке студента.</p>
                            <MyTextarea
                                title="Причина"
                                value={archiveComment}
                                setValue={setArchiveComment}
                                error={archiveCommentError}
                                setError={setArchiveCommentError}
                                placeholder="Например: студент завершил обучение"
                                rows={5}
                            />
                            <section className={classes.archiveCommentButtons}>
                                <MyButtonSecondary onClick={() => setOpen(false)}>
                                    Отмена
                                </MyButtonSecondary>
                                <MyButton onClick={onArchivePrepare}>
                                    Продолжить
                                </MyButton>
                            </section>
                        </section>
                    }
                </Modal>
            </section>
        </section>
    )
}
